import type { ReactNode } from 'react'
import { ArrowDown, ArrowUp } from 'lucide-react'

/* ===========================================================================
 *  De tabel
 *
 *  Eén tabel voor elk scherm: de verkenner, live, het grootboek, de
 *  vergelijking. De kop blijft staan als je scrolt, een kolom met een bedrag
 *  staat rechts met de cijfers onder elkaar, en een klik op een kop sorteert
 *  (als het scherm dat wil). Breed mag: een rij uit Exact heeft er dertig
 *  velden, en dan scrolt de tabel opzij in plaats van alles samen te persen.
 * =========================================================================== */

export interface TabelKolom<T> {
  sleutel: string
  label: ReactNode
  /** Tooltip op de kop: bij velden van Exact de naam van Exact. */
  titel?: string
  rechts?: boolean
  breedte?: number
  /** Blijft links staan bij opzij scrollen. */
  vast?: boolean
  cel: (r: T, i: number) => ReactNode
  voet?: ReactNode
  /** Mag op deze kolom gesorteerd worden? Standaard ja als er een onSorteer is. */
  sorteerbaar?: boolean
}

export function Tabel<T>({
  kolommen, rijen, sleutel, onKlik, sorteer, onSorteer, leeg, klein, actief, rijKlasse,
}: {
  kolommen: TabelKolom<T>[]
  rijen: T[]
  sleutel: (r: T, i: number) => string
  onKlik?: (r: T) => void
  sorteer?: { sleutel: string; richting: 'op' | 'af' } | null
  onSorteer?: (sleutel: string) => void
  leeg?: ReactNode
  klein?: boolean
  actief?: string | null
  rijKlasse?: (r: T) => string
}) {
  const heeftVoet = kolommen.some((k) => k.voet !== undefined)
  return (
    <div className={`tabel-houder ${klein ? 'klein' : ''}`}>
      <table className="tabel">
        <thead>
          <tr>
            {kolommen.map((k) => {
              const kan = !!onSorteer && k.sorteerbaar !== false
              const hier = sorteer?.sleutel === k.sleutel
              return (
                <th key={k.sleutel} title={k.titel}
                  className={`${k.rechts ? 'rechts' : ''} ${k.vast ? 'vast' : ''} ${kan ? 'kan-sorteren' : ''}`}
                  style={k.breedte ? { minWidth: k.breedte, width: k.breedte } : undefined}
                  onClick={kan ? () => onSorteer!(k.sleutel) : undefined}
                  aria-sort={hier ? (sorteer!.richting === 'op' ? 'ascending' : 'descending') : undefined}>
                  <span className="th-in">
                    {k.label}
                    {hier && (sorteer!.richting === 'op' ? <ArrowUp size={12} /> : <ArrowDown size={12} />)}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {rijen.map((r, i) => {
            const s = sleutel(r, i)
            return (
              <tr key={s} onClick={onKlik ? () => onKlik(r) : undefined}
                className={`${onKlik ? 'klikbaar' : ''} ${actief === s ? 'actief' : ''} ${rijKlasse?.(r) ?? ''}`}>
                {kolommen.map((k) => (
                  <td key={k.sleutel} className={`${k.rechts ? 'rechts' : ''} ${k.vast ? 'vast' : ''}`}>{k.cel(r, i)}</td>
                ))}
              </tr>
            )
          })}
          {!rijen.length && (
            <tr className="leeg-rij"><td colSpan={kolommen.length}>{leeg ?? 'Niets gevonden.'}</td></tr>
          )}
        </tbody>
        {heeftVoet && rijen.length > 0 && (
          <tfoot>
            <tr>
              {kolommen.map((k) => (
                <td key={k.sleutel} className={`${k.rechts ? 'rechts' : ''} ${k.vast ? 'vast' : ''}`}>{k.voet ?? ''}</td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  )
}
